import { Checkbox } from "@/components/ui/checkbox";
import {
  Action,
  RewardsScreen,
  UserJourney,
  useRewardsAnalytics,
} from "@/context/AnalyticsProvider";
import type { GameQuest } from "@/domain/gems-game/domain/lib/Sweepstake";
import { onDarkBase } from "@biom3/design-tokens";
import { Badge, HeroCard } from "@biom3/react";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { formatDistanceToNow, isPast } from "date-fns";
import { Check, Clock, RotateCcw, TrophyIcon, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useMemo } from "react";

interface GameQuestsCardProps {
  gameName: string;
  gameIcon?: string;
  gameSlug?: string;
  featured?: boolean;
  gameId?: string;
  quests: GameQuest[];
}

export const GameQuestsCard = ({
  gameName,
  gameIcon,
  gameSlug,
  featured,
  gameId,
  quests,
}: GameQuestsCardProps) => {
  const { track } = useRewardsAnalytics();

  const handleGameClick = () => {
    track({
      action: Action.PRESSED,
      extras: {
        gameName,
        questName: `${gameName} - Game Quest`,
        screen: RewardsScreen.PROGRESS_BAR,
        userJourney: UserJourney.CLAIM,
        gameId,
      },
    });
  };

  const handleQuestClick = (quest: GameQuest) => {
    track({
      action: Action.PRESSED,
      extras: {
        gameName,
        questName: quest.name,
        screen: RewardsScreen.PROGRESS_BAR,
        userJourney: UserJourney.CLAIM,
        gameId,
        externalId: quest.externalId,
      },
    });
  };
  const getShortAvailabilityTime = (nextAvailableAt: string) => {
    const nextTime = new Date(nextAvailableAt);
    const now = new Date();

    if (isPast(nextTime)) {
      return "";
    }

    const totalMinutes = Math.ceil(
      (nextTime.getTime() - now.getTime()) / (1000 * 60),
    );
    const hoursUntil = Math.floor(totalMinutes / 60);
    const minutesUntil = totalMinutes % 60;

    if (hoursUntil < 1) {
      return `${minutesUntil}m`;
    }
    if (hoursUntil <= 24) {
      return minutesUntil > 0
        ? `${hoursUntil}h ${minutesUntil}m`
        : `${hoursUntil}h`;
    }

    // If beyond 24 hours, show days
    const daysUntil = Math.ceil(hoursUntil / 24);
    return `${daysUntil}d`;
  };

  const isQuestEnded = useCallback(
    (endDate?: string) => (endDate ? new Date(endDate) < new Date() : false),
    [],
  );

  const formatLastCompletedTime = useCallback((lastCompletedAt?: string) => {
    if (!lastCompletedAt) {
      return "Never completed";
    }
    try {
      return `Last completed: ${formatDistanceToNow(new Date(lastCompletedAt), { addSuffix: true })}`;
    } catch {
      return "Last completed: Unknown";
    }
  }, []);

  const questGroups = useMemo(() => {
    const completed: GameQuest[] = [];
    const recurring: GameQuest[] = [];
    const notPlayed: GameQuest[] = [];
    const ended: GameQuest[] = [];

    for (const quest of quests) {
      if (isQuestEnded(quest.endDate)) {
        ended.push(quest);
      } else if (!quest.canStillCompleteThisPeriod) {
        completed.push(quest);
      } else if (quest.canStillCompleteThisPeriod && quest.refreshPeriodHours) {
        recurring.push(quest);
      } else {
        notPlayed.push(quest);
      }
    }

    return { completed, recurring, notPlayed, ended };
  }, [quests, isQuestEnded]);

  const completionStatus = useMemo(() => {
    const completedCount = questGroups.completed.length;
    const totalQuests = quests.length;
    const playsLeft =
      questGroups.recurring.length + questGroups.notPlayed.length;

    return {
      completed: completedCount,
      total: totalQuests,
      playsLeft: playsLeft,
    };
  }, [questGroups, quests]);

  if (quests.length === 0) {
    return null;
  }

  const listItemContent = (
    <Box
      sx={{
        position: "relative",
        padding: onDarkBase.spacing.x4,
        borderRadius: onDarkBase.borderRadius.x4,
        backgroundColor: onDarkBase.color.neutral[900],
        border: `1px solid ${featured ? "#FFE60080" : onDarkBase.color.translucent.standard[200]}`,
        zIndex: 0,
        ...(featured && {
          border: "1px solid transparent",
          boxShadow: "0 0 20px #F191FA40, 0 4px 12px rgba(0, 0, 0, 0.3)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: "inherit",
            padding: "1px",
            background:
              "linear-gradient(105.76deg, rgb(241, 145, 250) -46.96%, rgb(131, 227, 240) 99.99%)",
            "-webkit-mask":
              "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            "-webkit-mask-composite": "xor",
            maskComposite: "exclude",
            zIndex: -1,
          },
        }),
      }}
    >
      {/* Game Header */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
        <Box
          sx={{
            width: 35,
            height: 35,
            borderRadius: onDarkBase.borderRadius.x3,
            border: gameIcon
              ? `2px solid ${onDarkBase.color.translucent.standard[300]}`
              : "none",
            backgroundColor: gameIcon
              ? onDarkBase.color.neutral[700]
              : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <Image
            src={gameIcon || "/images/rewards/playgame.svg"}
            alt={gameIcon ? gameName : "Play Game"}
            width={gameIcon ? 32 : 40}
            height={gameIcon ? 32 : 40}
            style={{
              objectFit: "contain",
            }}
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Stack direction="column" justifyContent="space-between">
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  fontSize: "1rem",
                  color: onDarkBase.color.text.body.primary,
                }}
              >
                {gameName}
              </Typography>
              {featured && (
                <Badge
                  badgeContent="Featured"
                  variant="emphasis"
                  motionProfile="gentle"
                />
              )}
            </Stack>

            {/* Completion Status Badge */}
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.70rem",
                color: onDarkBase.color.text.body.secondary,
              }}
            >
              {completionStatus.completed}/{completionStatus.total} completed
            </Typography>
          </Stack>

          {/* Progress Bar */}
          <Box sx={{ mt: 1 }}>
            <Box
              sx={{
                width: "100%",
                height: "4px",
                backgroundColor: onDarkBase.color.translucent.standard[200],
                borderRadius: "2px",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${(completionStatus.completed / completionStatus.total) * 100}%`,
                  height: "100%",
                  backgroundColor: onDarkBase.color.status.success.bright,
                  borderRadius: "2px",
                  transition: "width 0.3s ease-in-out",
                }}
              />
            </Box>
          </Box>
        </Box>
      </Stack>

      <Stack spacing={0.4} sx={{ ml: 0.5 }}>
        {quests.map((quest) => {
          const isCompleted = questGroups.completed.includes(quest);
          const isRecurring = questGroups.recurring.includes(quest);
          const isEnded = isQuestEnded(quest.endDate);
          const isNotAvailable =
            (isRecurring &&
              quest.nextAvailableAt &&
              !isPast(new Date(quest.nextAvailableAt))) ||
            isEnded;
          const questNameStyle = {
            fontSize: "14px",
            color:
              isEnded || isNotAvailable
                ? onDarkBase.color.text.body.secondary
                : isCompleted
                  ? onDarkBase.color.status.success.bright
                  : onDarkBase.color.text.body.primary,
            maxWidth: "450px",
            overflow: "hidden",
            textDecoration: isEnded ? "line-through" : "",
            textOverflow: "ellipsis",
            lineHeight: 1.3,
          };

          /* TODO: pass in isSocial flag from the backend for social quests */
          const isSocialQuest = [
            "engagement",
            "engage",
            "discord",
            "telegram",
            "post",
            "twitter",
          ].some((word) => quest.externalId.includes(word));

          return (
            <Box
              key={quest.externalId}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 0.5,
                px: 1,
                borderRadius: onDarkBase.borderRadius.x2,
                cursor: "pointer",
                border: "1px solid transparent",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: isCompleted
                    ? `${onDarkBase.color.status.success.bright}20`
                    : isEnded
                      ? `${onDarkBase.color.text.body.secondary}15`
                      : onDarkBase.color.translucent.standard[100],
                  transform: "translateY(-1px)", // Subtle lift effect
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                },
              }}
              onClick={() => handleQuestClick(quest)}
            >
              {/* Left side: Icon + Quest Info */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flex: 1, minWidth: 0 }} // minWidth: 0 allows text truncation
              >
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{
                    flex: 1,
                    minWidth: 0, // Allow text truncation
                    color: isCompleted
                      ? onDarkBase.color.status.success.bright
                      : onDarkBase.color.text.body.primary,
                  }}
                >
                  {/* Conditional icon/checkbox based on quest state */}
                  <Box
                    sx={{
                      display: "flex",
                    }}
                  >
                    {isRecurring && isNotAvailable && !isEnded ? (
                      <Clock
                        size={14}
                        style={{ color: onDarkBase.color.text.body.secondary }}
                      />
                    ) : isEnded ? (
                      <X size={14} className="text-red-500" />
                    ) : isCompleted ? (
                      <Check size={14} className="text-green-500" />
                    ) : (
                      <Checkbox
                        checked={isCompleted}
                        disabled={!!isNotAvailable}
                        onCheckedChange={() => {
                          handleQuestClick(quest);
                        }}
                        className="rounded-full w-[14px] h-[14px]"
                      />
                    )}
                  </Box>

                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {!isCompleted && quest.lastCompletedAt ? (
                      <Tooltip
                        title={formatLastCompletedTime(quest.lastCompletedAt)}
                        placement="top"
                        arrow
                      >
                        <Stack direction="column">
                          <Typography variant="body1" sx={questNameStyle}>
                            {quest.name}
                          </Typography>
                          {isSocialQuest && (
                            <Typography
                              variant="body1"
                              sx={questNameStyle}
                              style={{
                                color: onDarkBase.color.status.attention.dim,
                              }}
                            >
                              Only high quality contents count, please follow
                              community guidelines.
                            </Typography>
                          )}
                        </Stack>
                      </Tooltip>
                    ) : (
                      <Stack direction="column">
                        <Typography variant="body1" sx={questNameStyle}>
                          {quest.name}
                        </Typography>

                        {isSocialQuest && (
                          <Typography
                            variant="body1"
                            sx={questNameStyle}
                            style={{
                              color: onDarkBase.color.status.attention.dim,
                            }}
                          >
                            Only high quality contents count, please follow
                            community guidelines.
                          </Typography>
                        )}
                      </Stack>
                    )}
                    {/* Recurring icon for recurring quests */}
                    {isRecurring && (
                      <Tooltip title="Recurring quest" placement="top" arrow>
                        <RotateCcw
                          size={16}
                          style={{
                            color: onDarkBase.color.text.body.secondary,
                            flexShrink: 0,
                          }}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                </Stack>

                {/* Center: Quest Status Chips */}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ flexShrink: 0 }}
                >
                  {quest.timesCompleted !== undefined &&
                    quest.timesCompleted > 0 &&
                    isRecurring && (
                      <Chip
                        size="medium"
                        label={
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <HeroCard.Chip.Icon
                              icon="Trophy"
                              variant="bold"
                              size="large"
                              sx={{
                                color: onDarkBase.color.text.caption.secondary,
                              }}
                            />
                            <span>x {quest.timesCompleted}</span>
                          </Stack>
                        }
                        sx={{
                          backgroundColor: `${onDarkBase.color.text.caption.secondary}20`,
                          color: onDarkBase.color.text.caption.secondary,
                          minWidth: "auto",
                          height: "28px",
                          fontSize: "0.875rem",
                        }}
                      />
                    )}

                  {isCompleted && quest.lastCompletedAt && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        color: onDarkBase.color.text.caption.secondary,
                        fontWeight: 400,
                      }}
                    >
                      {formatDistanceToNow(new Date(quest.lastCompletedAt), {
                        addSuffix: true,
                      })}
                    </Typography>
                  )}
                </Stack>
              </Stack>

              {/* Right side: Start Button or Clock + Time */}
              {isRecurring && isNotAvailable && !isEnded && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{
                    ml: 2,
                    minWidth: "60px",
                    flexShrink: 0,
                    color: onDarkBase.color.status.guidance.bright,
                  }}
                >
                  <Clock
                    size={14}
                    style={{ color: onDarkBase.color.status.guidance.bright }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 500,
                    }}
                  >
                    {getShortAvailabilityTime(quest.nextAvailableAt)}
                  </Typography>
                </Stack>
              )}
              <Stack marginLeft="12px" alignSelf="right">
                <Typography
                  align="right"
                  variant="body2"
                  sx={{
                    color: isCompleted
                      ? onDarkBase.color.status.success.bright
                      : onDarkBase.color.text.status.guidance.primary,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <TrophyIcon size={16} className="mr-1" />
                  <span>+{quest.weeklyPoints}</span>
                </Typography>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );

  return gameSlug ? (
    <Link
      href={`/games/${gameSlug}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: "none" }}
      onClick={handleGameClick}
    >
      {listItemContent}
    </Link>
  ) : (
    listItemContent
  );
};
